import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import API_BASE_URL from "../config";
import { MapPin, Users, MessageCircle, Mountain, ArrowRight, Star } from "lucide-react";

const API = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

const PLACEHOLDER_IMGS = [
  "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?w=800&q=80",
  "https://images.unsplash.com/photo-1588694096057-0744db600eb9?w=800&q=80",
  "https://images.unsplash.com/photo-1589136777351-fdc9c9cb1667?w=800&q=80",
];

export default function Landing() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/homepage`, { withCredentials: true })
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getPlaceImage = (place, idx) => {
    if (place.images?.length > 0) {
      const img = place.images[0];
      if (typeof img === "string" && /^https?:\/\//i.test(img)) return img;
      return `${API_BASE_URL}${img.startsWith("/") ? "" : "/"}${img}`;
    }
    return PLACEHOLDER_IMGS[idx % PLACEHOLDER_IMGS.length];
  };

  return (
    <div style={{ background: '#f2f0eb', minHeight: '100vh', paddingBottom: 80 }}>
      {/* HERO - dark forest */}
      <section style={{ background: '#1a3a1a', padding: '56px 20px 44px' }}>
        <div style={{ maxWidth: 1024, margin: '0 auto' }}>
          <p style={{ color: '#1D9E75', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>
            🏔 Uttarakhand Travel
          </p>
          <h1 style={{ color: 'white', fontSize: 32, fontWeight: 700, lineHeight: 1.2, marginBottom: 10 }}>
            Explore the<br />Land of Gods
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginBottom: 28, lineHeight: 1.6 }}>
            {data?.stats ? `${data.stats.places} verified places across Uttarakhand` : 'Discover hidden gems of Uttarakhand'}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link
              to="/places"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                border: '1.5px solid rgba(255,255,255,0.6)', color: 'white',
                borderRadius: 999, padding: '10px 20px', fontSize: 14,
                fontWeight: 500, textDecoration: 'none'
              }}
            >
              📍 Explore Places
            </Link>
            <Link
              to="/trip-ai"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                border: '1.5px solid rgba(255,255,255,0.6)', color: 'white',
                borderRadius: 999, padding: '10px 20px', fontSize: 14,
                fontWeight: 500, textDecoration: 'none'
              }}
            >
              ✨ Plan with AI
            </Link>
          </div>
        </div>
      </section>

      {/* STATS ROW */}
      {data?.stats && (
        <section style={{ padding: '20px 20px 0', maxWidth: 1024, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[
              { value: data.stats.places, label: 'Places' },
              { value: data.stats.posts, label: 'Posts' },
              { value: data.stats.users, label: 'Travelers' },
            ].map((s, i) => (
              <div
                key={i}
                style={{
                  background: 'white', borderRadius: 16, padding: '16px 12px',
                  textAlign: 'center', border: '1px solid rgba(0,0,0,0.06)'
                }}
              >
                <div style={{ fontSize: 24, fontWeight: 700, color: '#1a3a1a' }}>{s.value}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FEATURED PLACES */}
      {data?.popular_places?.length > 0 && (
        <section style={{ padding: '24px 20px 0', maxWidth: 1024, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1a2e1a', margin: 0 }}>Featured places</h2>
            <Link to="/places" style={{ fontSize: 13, color: '#1D9E75', textDecoration: 'none', fontWeight: 500 }}>
              See all →
            </Link>
          </div>
          <div
            style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}
            className="scrollbar-hide"
          >
            {data.popular_places.slice(0, 8).map((place, i) => (
              <Link
                key={place.id}
                to={`/places/${place.id}`}
                style={{ textDecoration: 'none', flexShrink: 0, width: 110 }}
              >
                <div style={{
                  width: 110, height: 90, borderRadius: 14, overflow: 'hidden',
                  background: 'linear-gradient(135deg, #1a3a1a, #0f4a3c)', marginBottom: 8
                }}>
                  <img
                    src={getPlaceImage(place, i)}
                    alt={place.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1a2e1a', lineHeight: 1.3 }}>{place.name}</div>
                <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'capitalize' }}>
                  {(place.category || '').replace('_', ' ')}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* GIRI AI STRIP */}
      <section style={{ padding: '20px 20px 0', maxWidth: 1024, margin: '0 auto' }}>
        <Link
          to="/giri-ai"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: '#1a3a1a', borderRadius: 16, padding: '14px 18px', textDecoration: 'none'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 24 }}>✨</div>
            <div>
              <div style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>Giri AI — plan your trip</div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>Ask anything about Uttarakhand</div>
            </div>
          </div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 20 }}>›</div>
        </Link>
      </section>

      {/* POPULAR PLACES SECTION */}
      {data?.popular_places?.length > 0 && (
        <section style={{ padding: '28px 20px 0', maxWidth: 1024, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1a2e1a', margin: 0 }}>Popular destinations</h2>
            <Link to="/places" style={{ fontSize: 13, color: '#1D9E75', textDecoration: 'none', fontWeight: 500 }}>
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.popular_places.slice(0, 6).map((place, i) => (
              <Link
                key={place.id}
                to={`/places/${place.id}`}
                style={{ textDecoration: 'none' }}
              >
                <div style={{
                  background: 'white', borderRadius: 16, overflow: 'hidden',
                  border: '1px solid rgba(0,0,0,0.06)', display: 'flex', alignItems: 'stretch'
                }}>
                  <div style={{ width: 90, height: 80, flexShrink: 0, background: 'linear-gradient(135deg, #1a3a1a, #0f4a3c)' }}>
                    <img
                      src={getPlaceImage(place, i)}
                      alt={place.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div style={{ padding: '12px 14px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1a2e1a', marginBottom: 3 }}>{place.name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MapPin size={11} style={{ color: '#1D9E75' }} />
                      {place.address || 'Uttarakhand'}
                    </div>
                    {place.avg_rating > 0 && (
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Star size={10} style={{ color: '#f59e0b', fill: '#f59e0b' }} />
                        {place.avg_rating}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', padding: '0 14px 0 0' }}>
                    <span style={{ color: '#1D9E75', fontSize: 18 }}>›</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA SECTION */}
      <section style={{ padding: '28px 20px 0', maxWidth: 1024, margin: '0 auto' }}>
        <div style={{
          background: '#1a3a1a', borderRadius: 20, padding: '28px 24px', textAlign: 'center'
        }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🏔️</div>
          <h2 style={{ color: 'white', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
            Ready to share your journey?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
            Join thousands of travelers who trust this community for authentic Uttarakhand experiences.
          </p>
          <Link
            to={user && user !== false ? "/channels" : "/register"}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#1D9E75', color: 'white', borderRadius: 999,
              padding: '12px 24px', fontSize: 14, fontWeight: 600, textDecoration: 'none'
            }}
          >
            {user && user !== false ? "Go to Community" : "Get Started"} <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ marginTop: 32, padding: '24px 20px', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
        <div style={{ maxWidth: 1024, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
            <Mountain size={18} style={{ color: '#1a3a1a' }} />
            <span style={{ fontSize: 15, fontWeight: 700, color: '#1a3a1a' }}>Uttarakhand Travel</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginBottom: 12 }}>
            <Link to="/about" style={{ fontSize: 12, color: '#6b7280', textDecoration: 'none' }}>About</Link>
            <Link to="/contact" style={{ fontSize: 12, color: '#6b7280', textDecoration: 'none' }}>Contact</Link>
            <Link to="/privacy" style={{ fontSize: 12, color: '#6b7280', textDecoration: 'none' }}>Privacy</Link>
          </div>
          <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af' }}>
            © {new Date().getFullYear()} Uttarakhand Travel Community
          </p>
        </div>
      </footer>
    </div>
  );
}
